/**
 * Signing in, when accounts are on (core/session.ts).
 *
 * `AccountsGate` wraps the whole app. Before anyone has signed in it shows only
 * this screen, so nothing asks the engine for what it would refuse. Once the app
 * is showing it stays mounted even if the session runs out: the sign-in then
 * covers it, so On air keeps playing while the presenter signs in again.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { engineInfo, isWeb } from "../platform";
import { Offline } from "./Offline";
import { refreshSession, signIn, useSession } from "../core/session";
import { bus } from "../core/bus";

export function AccountsGate({ children }: { children: ReactNode }) {
  const s = useSession();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    void engineInfo().then((info) => refreshSession(info.url));
    // A role or person changed (another window, an Administrator): read who this is again.
    return bus.on("auth.*", () => void engineInfo().then((info) => refreshSession(info.url)));
  }, []);
  // In a browser, not before the engine has answered: until then, the "cannot connect" screen.
  const ready = s.known && (!s.enabled || !!s.user) && !(isWeb() && s.reachable === false);
  useEffect(() => {
    if (ready) setShown(true);
  }, [ready]);
  if (!s.known) return null;
  // In a browser (the installed app included) with no engine to talk to: Nova's own screen.
  if (isWeb() && s.reachable === false && !shown) {
    // Shown only before Nova first opened in this tab: once open, it shows its own "engine offline" state.
    return <Offline onRetry={() => engineInfo().then((info) => refreshSession(info.url))} />;
  }
  return (
    <>
      {(shown || ready) && children}
      {s.enabled && !s.user && <SignIn over={shown} ended={shown && !s.signedOut} />}
    </>
  );
}

export function SignIn({ over = false, ended = false }: { over?: boolean; ended?: boolean }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(await signIn(username.trim(), password, remember));
    setBusy(false);
  };
  return (
    <div className={over ? "nv-modal nv-signin-over" : "nv-signin-page"} role="dialog" aria-modal="true" aria-label="Sign in to Nova">
      <form className="nv-signin" onSubmit={(e) => void submit(e)}>
        <span className="nv-brand nv-signin-brand">
          <i aria-hidden="true" />
          Nova
        </span>
        <h1>{ended ? "Sign in again" : "Sign in"}</h1>
        {ended && <p className="nv-dim">Your session ended. Everything keeps playing while you sign in.</p>}
        {over && !ended && <p className="nv-dim">Signed out. What is on air keeps playing.</p>}
        <label>
          <span>Username</span>
          <input className="nv-input" autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
        </label>
        <label>
          <span>Password</span>
          <input className="nv-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="nv-signin-remember">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>Stay signed in on this computer</span>
        </label>
        {error && <p className="nv-signin-error" role="alert">{error}</p>}
        <button className="nv-btn primary" type="submit" disabled={busy || !username.trim() || !password}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
